import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { fetchJson } from '../api.js';
import { tnd } from '../lib/format.js';

/**
 * Printable picking list + shipping note for a shop-manager order.
 * Two print modes via ?mode=picking|shipping. Pure HTML + @media print —
 * the browser print dialog produces the PDF (no extra dependency).
 */

interface PrintOrder {
  readonly number: string;
  readonly customerName: string | null;
  readonly dateCreated: string | null;
  readonly total: number | null;
  readonly billingPhone: string | null;
  readonly billingAddress: string | null;
  readonly billingCity: string | null;
  readonly billingGov: string | null;
  readonly jetpackCode: string | null;
  readonly fragile?: boolean | null;
  readonly ouvrir?: boolean | null;
  readonly lineItems: readonly { sku: string | null; name: string | null; qty: number; price: number | null }[];
}

export function ShopOrderPrintPage(): React.ReactElement {
  const params = useParams<{ wooId: string; mode?: string }>();
  const rawMode = params.mode ?? 'picking';
  const mode = rawMode === 'shipping' ? 'shipping' : rawMode === 'thermal' ? 'thermal' : 'picking';
  const wooId = Number(params.wooId);

  const query = useQuery({
    queryKey: ['shop-order-print', wooId],
    queryFn: () =>
      fetchJson<PrintOrder>(`/api/v1/shop-manager/orders/${wooId}` as string).then((d: unknown) => {
        const detail = d as { order: PrintOrder };
        return detail.order;
      }),
    enabled: Number.isInteger(wooId),
  });

  const order = query.data;

  if (query.isPending) {
    return <p className="p-8 text-sm text-ink-soft">Préparation du document…</p>;
  }
  if (order === undefined) {
    return <p className="p-8 text-sm text-ink">Commande introuvable.</p>;
  }

  const rawItems = order.lineItems;
  const items = Array.isArray(rawItems) ? rawItems : [];
  const totalQty = items.reduce((sum, li) => sum + (li.qty ?? 0), 0);

  // Thermal 100×150 mm label (Zebra/Xprinter): compact single-label layout,
  // @page drives the printer — screen preview is scaled to match.
  if (mode === 'thermal') {
    return (
      <>
        <style>{`@page { size: 100mm 150mm; margin: 3mm; }`}</style>
        <div className="mx-auto bg-white text-black print:m-0" style={{ width: '94mm', minHeight: '144mm' }}>
          <div className="border-b-2 border-black pb-1 text-center">
            <p className="text-xl font-black leading-none tracking-tight">MaPara</p>
            <p className="text-[10px]">maparatunisie.tn · Paiement à la livraison</p>
          </div>
          <p className="mt-1 text-center font-mono text-sm font-bold">Commande #{order.number}</p>
          <div className="mt-1 border-2 border-black p-1.5">
            <p className="text-base font-bold leading-tight">{order.customerName ?? '-'}</p>
            <p className="font-mono text-base font-bold">{order.billingPhone ?? ''}</p>
            <p className="text-xs">
              {order.billingAddress ?? ''} {order.billingCity ?? ''} {order.billingGov ?? ''}
            </p>
          </div>
          <p className="mt-1 text-center text-3xl font-black">
            {order.total !== null ? `${Number(order.total).toFixed(3)} DT` : '—'}
          </p>
          <p className="text-center font-mono text-lg font-bold tracking-widest">
            {order.jetpackCode ?? 'SANS COLIS'}
          </p>
          <div className="mt-1 border-t border-black pt-1 text-[11px] leading-tight">
            {items.slice(0, 8).map((li, idx) => (
              <p key={`${li.sku ?? 'item'}-${idx}`} className="flex justify-between gap-2">
                <span className="truncate">{li.qty}× {li.name ?? '—'}</span>
              </p>
            ))}
            {items.length > 8 && <p>+{items.length - 8} article(s)…</p>}
          </div>
          <div className="mt-1 flex justify-between text-xs font-bold">
            <span>{totalQty} article(s)</span>
            <span>
              {order.fragile === true ? 'FRAGILE · ' : ''}
              {order.ouvrir === false ? 'NE PAS OUVRIR' : 'OUVRIR OK'}
            </span>
          </div>
          <button
            type="button"
            onClick={() => window.print()}
            className="mt-3 rounded border border-black px-4 py-2 text-sm font-bold print:hidden"
          >
            Imprimer (100×150)
          </button>
        </div>
      </>
    );
  }

  return (
    <div className="mx-auto max-w-[820px] bg-white p-8 text-black print:p-0" style={{ minHeight: '100vh' }}>
      <div className="mb-6 flex items-start justify-between border-b-2 border-black pb-4">
        <div>
          <p className="text-2xl font-bold">{mode === 'picking' ? 'PICKING LIST' : 'BON D’EXPÉDITION'}</p>
          <p className="mt-1 text-sm">
            Commande <strong>#{order.number}</strong>
            {order.dateCreated !== null && <> · {new Date(order.dateCreated).toLocaleDateString('fr-FR')}</>}
          </p>
        </div>
        <div className="text-right">
          <p className="text-3xl font-black tracking-tight">MaPara</p>
          <p className="text-xs">maparatunisie.tn</p>
        </div>
      </div>

      {mode === 'shipping' && (
        <div className="mb-6 grid grid-cols-2 gap-6">
          <div className="border-2 border-black p-4">
            <p className="mb-1 text-[10px] font-bold uppercase tracking-wider">Destinataire</p>
            <p className="text-lg font-bold">{order.customerName ?? '—'}</p>
            <p className="text-sm">{order.billingAddress ?? ''}</p>
            <p className="text-sm">
              {order.billingCity ?? ''} {order.billingGov ?? ''}
            </p>
            <p className="mt-1 font-mono text-sm">{order.billingPhone ?? ''}</p>
          </div>
          <div className="border-2 border-black p-4">
            <p className="mb-1 text-[10px] font-bold uppercase tracking-wider">Code-barres colis</p>
            <p className="font-mono text-2xl font-black">{order.jetpackCode ?? 'À CRÉER'}</p>
            <p className="mt-2 text-[10px] font-bold uppercase tracking-wider">Transporteur</p>
            <p className="text-sm font-bold">Jetpack</p>
            <p className="mt-2 text-[10px] font-bold uppercase tracking-wider">Paiement</p>
            <p className="text-sm font-bold">
              Contre remboursement : {order.total !== null ? `${order.total.toFixed(3)} DT` : '—'}
            </p>
          </div>
        </div>
      )}

      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b-2 border-black text-left">
            <th className="py-2 pr-2">SKU</th>
            <th className="py-2 pr-2">Article</th>
            {mode === 'picking' && <th className="py-2 pr-2 w-16 text-center">Pris</th>}
            <th className="py-2 pr-2 text-right">Qté</th>
            {mode === 'shipping' && <th className="py-2 text-right">Prix</th>}
          </tr>
        </thead>
        <tbody>
          {items.map((li, idx) => (
            <tr key={`${li.sku ?? 'item'}-${idx}`} className="border-b border-gray-300">
              <td className="py-2 pr-2 font-mono">{li.sku ?? '—'}</td>
              <td className="py-2 pr-2">{li.name ?? '—'}</td>
              {mode === 'picking' && <td className="py-2 pr-2 text-center border border-gray-400">☐</td>}
              <td className="py-2 pr-2 text-right font-bold">{li.qty}</td>
              {mode === 'shipping' && (
                <td className="py-2 text-right">{li.price !== null ? `${Number(li.price).toFixed(3)} DT` : '—'}</td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-4 flex items-center justify-between border-t-2 border-black pt-3">
        <p className="text-sm font-bold">{totalQty} article(s)</p>
        {mode === 'shipping' && (
          <p className="text-xl font-black">COD : {order.total !== null ? tnd(order.total) : '—'}</p>
        )}
      </div>

      <div className="mt-10 grid grid-cols-2 gap-8 text-xs">
        <div>
          <p className="mb-8">Signature préparateur :</p>
          <p className="border-t border-black w-40" />
        </div>
        <div>
          <p className="mb-8">Signature transporteur :</p>
          <p className="border-t border-black w-40" />
        </div>
      </div>

      <button
        type="button"
        onClick={() => window.print()}
        className="mt-8 hidden rounded border border-black px-4 py-2 text-sm font-bold print:hidden"
      >
        Imprimer
      </button>
      <p className="mt-6 text-center text-[10px] text-gray-500 print:hidden">
        Astuce : Ctrl+P puis « Enregistrer en PDF ». Fermez l’onglet pour revenir.
      </p>
    </div>
  );
}
